import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/PageHeader';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService from '@/lib/api/demoStudio';
import contentLibraryService, {
  type PromptReviewCandidate,
  type PromptReviewDecision,
} from '@/lib/api/contentLibrary';
import { getBrowserTimezone } from '@/lib/timezone';
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Clapperboard,
  Clock,
  ListChecks,
  Loader2,
  MonitorPlay,
  Play,
  RotateCw,
  Video,
  XCircle,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const buildDefaultRecordingName = (organizationName?: string | null) =>
  organizationName ? `${organizationName} demo recording` : 'Demo recording';

const REVIEW_DECISION_LABELS: Record<PromptReviewDecision, string> = {
  approve: 'Add to segment catalog',
  replace_matched: 'Replace match',
  library_only: 'Library only',
  ignore: 'Ignore',
};

const getDefaultDecision = (candidate: PromptReviewCandidate): PromptReviewDecision => {
  switch (candidate.recommended_action) {
    case 'possible_duplicate':
      return 'library_only';
    case 'possible_replacement':
      return candidate.matched_segment ? 'replace_matched' : 'approve';
    case 'library_only_candidate':
      return 'library_only';
    case 'new_candidate':
    default:
      return 'approve';
  }
};

const formatSessionTime = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatSegmentTimestamp = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '0:00';
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const recommendationBadgeCopy: Record<string, string> = {
  possible_duplicate: 'Possible duplicate',
  possible_replacement: 'Possible replacement',
  new_candidate: 'New candidate',
  library_only_candidate: 'Library only',
};

const VALID_REVIEW_DECISIONS = new Set<PromptReviewDecision>([
  'approve',
  'replace_matched',
  'library_only',
  'ignore',
]);

const isValidReviewDecision = (value: unknown): value is PromptReviewDecision =>
  typeof value === 'string' && VALID_REVIEW_DECISIONS.has(value as PromptReviewDecision);

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  complete: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Complete' },
  processing: { icon: Loader2, color: 'text-amber-400', label: 'Processing' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
  active: { icon: Circle, color: 'text-blue-400', label: 'Active' },
  abandoned: { icon: Clock, color: 'text-zinc-400', label: 'Expired' },
};

/* ------------------------------------------------------------------ */
/*  Animation variants                                                */
/* ------------------------------------------------------------------ */

const stagger = {
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const fadeUp = {
  initial: { opacity: 0, y: 12, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

const DemoStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { userData } = useAuth();
  const { toast } = useToast();
  const [launching, setLaunching] = React.useState(false);
  const [recordingTitle, setRecordingTitle] = React.useState('');
  const [initialUrl, setInitialUrl] = React.useState('');
  const [reviewSessionId, setReviewSessionId] = React.useState<string | null>(null);
  const [reviewDecisions, setReviewDecisions] = React.useState<
    Record<string, PromptReviewDecision>
  >({});

  React.useEffect(() => {
    setRecordingTitle((currentValue) =>
      currentValue.trim() ? currentValue : buildDefaultRecordingName(currentOrganization?.name),
    );
  }, [currentOrganization?.name]);

  /* ---- Queries ---- */

  const sessionsQuery = useQuery({
    queryKey: ['demo-studio-sessions', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('Organization required');
      const response = await demoStudioService.listSessions(currentOrganization.id, {
        limit: 12,
        offset: 0,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load demo studio sessions.');
      }
      return response.data;
    },
    enabled: Boolean(currentOrganization?.id),
    refetchInterval: 5000,
  });

  const reviewSession = React.useMemo(
    () => sessionsQuery.data?.sessions.find((s) => s.id === reviewSessionId) ?? null,
    [reviewSessionId, sessionsQuery.data?.sessions],
  );

  const reviewQuery = useQuery({
    queryKey: [
      'demo-studio-prompt-review',
      reviewSession?.organization_video_id,
      currentOrganization?.id,
    ],
    queryFn: async () => {
      if (!reviewSession?.organization_video_id || !currentOrganization?.id) {
        throw new Error('Video and organization required');
      }
      const response = await contentLibraryService.getVideoPromptReview(
        reviewSession.organization_video_id,
        currentOrganization.id,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load prompt review candidates.');
      }
      return response.data;
    },
    enabled: Boolean(reviewSession?.organization_video_id && currentOrganization?.id),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (!reviewQuery.data) {
      setReviewDecisions({});
      return;
    }
    const next: Record<string, PromptReviewDecision> = {};
    for (const c of reviewQuery.data.candidates) {
      next[c.segment_id] = isValidReviewDecision(c.review_decision)
        ? c.review_decision
        : getDefaultDecision(c);
    }
    setReviewDecisions(next);
  }, [reviewQuery.data]);

  /* ---- Mutations ---- */

  const applyReviewMutation = useMutation({
    mutationFn: async () => {
      if (!reviewSession?.organization_video_id || !currentOrganization?.id || !reviewQuery.data) {
        throw new Error('Review context unavailable');
      }
      const decisions = reviewQuery.data.candidates
        .filter((c) => c.review_status === 'review_pending')
        .map((c) => ({
          segment_id: c.segment_id,
          decision: reviewDecisions[c.segment_id] ?? getDefaultDecision(c),
        }));
      if (!decisions.length) throw new Error('No pending decisions to apply.');
      const response = await contentLibraryService.applyVideoPromptReview(
        reviewSession.organization_video_id,
        { organization_id: currentOrganization.id, decisions },
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to apply prompt review.');
      }
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Review applied',
        description: `${data.updated} segment${data.updated === 1 ? '' : 's'} updated.`,
      });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          'demo-studio-prompt-review',
          reviewSession?.organization_video_id,
          currentOrganization?.id,
        ],
      });
      setReviewSessionId(null);
    },
    onError: (error) => {
      toast({
        title: 'Review failed',
        description: error instanceof Error ? error.message : 'Failed to apply review.',
        variant: 'destructive',
      });
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await demoStudioService.retryFinalization(sessionId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to retry finalization.');
      }
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Retry started', description: 'Finalization is re-running in the background.' });
      void queryClient.invalidateQueries({
        queryKey: ['demo-studio-sessions', currentOrganization?.id],
      });
    },
    onError: (error) => {
      toast({
        title: 'Retry failed',
        description: error instanceof Error ? error.message : 'Failed to retry finalization.',
        variant: 'destructive',
      });
    },
  });

  /* ---- Handlers ---- */

  const handleLaunch = React.useCallback(async () => {
    if (!currentOrganization?.id) {
      toast({ title: 'No organization selected', variant: 'destructive' });
      return;
    }
    if (!recordingTitle.trim()) {
      toast({ title: 'Recording name required', variant: 'destructive' });
      return;
    }
    if (!initialUrl.trim()) {
      toast({ title: 'Starting page required', variant: 'destructive' });
      return;
    }

    setLaunching(true);
    try {
      const response = await demoStudioService.createSession({
        organization_id: currentOrganization.id,
        title: recordingTitle.trim(),
        timezone: userData?.timezone || getBrowserTimezone(),
        initial_url: initialUrl.trim(),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to launch Demo Studio.');
      }
      toast({ title: 'Studio launched', description: 'Browser session is ready.' });
      sessionStorage.setItem(
        `demo-studio-launch:${response.data.id}`,
        JSON.stringify({ title: recordingTitle.trim(), initialUrl: initialUrl.trim() }),
      );
      navigate(`/demo-studio/sessions/${response.data.id}`);
    } catch (error) {
      toast({
        title: 'Launch failed',
        description: error instanceof Error ? error.message : 'Failed to launch.',
        variant: 'destructive',
      });
    } finally {
      setLaunching(false);
    }
  }, [currentOrganization?.id, initialUrl, navigate, recordingTitle, toast, userData?.timezone]);

  const recentSessions = sessionsQuery.data?.sessions ?? [];
  const canLaunch =
    Boolean(currentOrganization?.id) &&
    recordingTitle.trim().length > 0 &&
    initialUrl.trim().length > 0;

  /* ---- Render ---- */

  return (
    <>
      <Helmet>
        <title>Studio | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          title={<>Product Studio <HelpTooltip text="Record product walkthroughs in a managed browser." /></>}
        />

        <motion.div
          className="flex-1 px-4 py-6 sm:px-6 md:px-8"
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          {/* ---- Launch Card ---- */}
          <motion.div variants={fadeUp}>
            <Card className="border card-border shadow-none overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto]">
                  {/* Left: form */}
                  <div className="p-5 space-y-4">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MonitorPlay className="h-4 w-4" />
                      </div>
                      <h2 className="text-sm font-medium text-foreground">New recording</h2>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-recording-title" className="text-xs">
                          Recording name
                        </Label>
                        <Input
                          id="demo-studio-recording-title"
                          type="text"
                          placeholder="Q2 product overview"
                          value={recordingTitle}
                          onChange={(e) => setRecordingTitle(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="demo-studio-initial-url" className="text-xs">
                          Starting page
                        </Label>
                        <Input
                          id="demo-studio-initial-url"
                          type="url"
                          placeholder="https://app.example.com/login"
                          value={initialUrl}
                          onChange={(e) => setInitialUrl(e.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                          className="h-9"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: launch */}
                  <div className="flex items-center border-t lg:border-t-0 lg:border-l border-border/60 bg-muted/20 px-8 py-5">
                    <motion.div
                      whileHover={canLaunch && !launching ? { scale: 1.02 } : undefined}
                      whileTap={canLaunch && !launching ? { scale: 0.97 } : undefined}
                    >
                      <Button
                        size="lg"
                        onClick={handleLaunch}
                        disabled={launching || !canLaunch}
                        className="gap-2 px-6 h-10"
                      >
                        {launching ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Launching…
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4" />
                            Launch Studio
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ---- Recordings ---- */}
          <motion.div variants={fadeUp} className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-medium text-foreground">Recordings</h2>
                {sessionsQuery.isFetching && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
              {recentSessions.length > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {recentSessions.length} session{recentSessions.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {sessionsQuery.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[128px] rounded-xl border border-border/50 bg-muted/20 animate-pulse"
                  />
                ))}
              </div>
            ) : recentSessions.length === 0 ? (
              <Card className="border border-dashed card-border shadow-none">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/50 mb-3">
                    <Video className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                  <p className="text-sm text-muted-foreground">No recordings yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence mode="popLayout">
                  {recentSessions.map((session, idx) => {
                    const hasVideo = Boolean(session.organization_video_id);
                    const cfg = STATUS_CONFIG[session.status] ?? {
                      icon: Circle,
                      color: 'text-muted-foreground',
                      label: session.status,
                    };
                    const StatusIcon = cfg.icon;

                    return (
                      <motion.div
                        key={session.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                          duration: 0.3,
                          delay: idx * 0.04,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      >
                        <Card className="group border card-border shadow-none hover:border-primary/20 transition-colors duration-200">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-medium text-foreground truncate">
                                  {session.title || 'Untitled recording'}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <StatusIcon
                                    className={`h-3 w-3 ${cfg.color} ${session.status === 'processing' ? 'animate-spin' : ''}`}
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    {cfg.label}
                                  </span>
                                </div>
                              </div>
                              <span className="text-xs text-muted-foreground/70 shrink-0 tabular-nums">
                                {formatSessionTime(session.updated_at)}
                              </span>
                            </div>

                            {hasVideo && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 h-5 gap-1"
                              >
                                <CheckCircle2 className="h-2.5 w-2.5" />
                                Video ready
                              </Badge>
                            )}

                            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
                              {session.organization_video_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs px-2 gap-1.5"
                                  onClick={() =>
                                    navigate(
                                      `/content-library/video/${session.organization_video_id}`,
                                    )
                                  }
                                >
                                  <Clapperboard className="h-3 w-3" />
                                  View
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs px-2 gap-1.5"
                                disabled={!hasVideo}
                                onClick={() => setReviewSessionId(session.id)}
                              >
                                <ListChecks className="h-3 w-3" />
                                Review
                              </Button>
                              {(session.status === 'processing' || session.status === 'failed') &&
                                !session.organization_video_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs px-2 gap-1.5 text-amber-500 hover:text-amber-600"
                                  disabled={retryMutation.isPending && retryMutation.variables === session.id}
                                  onClick={() => retryMutation.mutate(session.id)}
                                >
                                  <RotateCw className={`h-3 w-3 ${retryMutation.isPending && retryMutation.variables === session.id ? 'animate-spin' : ''}`} />
                                  Retry
                                </Button>
                              )}
                              {hasVideo && (
                                <ArrowRight className="h-3 w-3 text-muted-foreground/40 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* ---- Review Dialog ---- */}
      <Dialog
        open={Boolean(reviewSessionId)}
        onOpenChange={(open) => !open && setReviewSessionId(null)}
      >
        <DialogContent className="max-w-5xl overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>Review segmented assets</DialogTitle>
            <DialogDescription>
              Decide which clips update the demo catalog and which stay in the library.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[75vh] overflow-y-auto px-6 py-5">
            {reviewQuery.isLoading ? (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading candidates…
              </div>
            ) : reviewQuery.data?.candidates.length ? (
              <div className="space-y-4">
                {reviewQuery.data.candidates.map((candidate) => {
                  const decision =
                    reviewDecisions[candidate.segment_id] ?? getDefaultDecision(candidate);
                  const pending = candidate.review_status === 'review_pending';
                  return (
                    <div
                      key={candidate.segment_id}
                      className="rounded-2xl border border-border/70 p-4"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">
                              {candidate.label || 'Untitled segment'}
                            </span>
                            <Badge variant={pending ? 'secondary' : 'outline'}>
                              {candidate.review_status || 'unknown'}
                            </Badge>
                            {candidate.recommended_action && (
                              <Badge variant="outline">
                                {recommendationBadgeCopy[candidate.recommended_action] ??
                                  candidate.recommended_action}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {candidate.description || candidate.reason || 'No description.'}
                          </p>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {candidate.start_time.toFixed(1)}s – {candidate.end_time.toFixed(1)}s
                            {typeof candidate.confidence === 'number'
                              ? ` · ${candidate.confidence.toFixed(2)}`
                              : ''}
                          </div>

                          {candidate.matched_segment && (
                            <div className="rounded-xl border border-border/70 bg-muted/20 p-3 mt-2">
                              <div className="mb-3 flex flex-wrap items-center gap-2">
                                <Badge variant="secondary">
                                  {candidate.recommended_action === 'possible_duplicate'
                                    ? 'Possible duplicate'
                                    : 'Possible replacement'}
                                </Badge>
                              </div>
                              <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                                <div className="overflow-hidden rounded-lg border border-border/70 bg-muted/30">
                                  {candidate.matched_segment.thumbnail_url ? (
                                    <img
                                      src={candidate.matched_segment.thumbnail_url}
                                      alt={candidate.matched_segment.label || 'Segment thumbnail'}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex aspect-video items-center justify-center text-xs text-muted-foreground">
                                      No preview
                                    </div>
                                  )}
                                </div>
                                <div className="space-y-1.5 text-sm">
                                  <div className="font-medium text-foreground">
                                    {candidate.matched_segment.label || 'Untitled'}
                                  </div>
                                  {candidate.matched_segment.video_title && (
                                    <div className="text-muted-foreground">
                                      From:{' '}
                                      <span className="text-foreground">
                                        {candidate.matched_segment.video_title}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-muted-foreground tabular-nums">
                                    {formatSegmentTimestamp(candidate.matched_segment.start_time)} –{' '}
                                    {formatSegmentTimestamp(candidate.matched_segment.end_time)}
                                    {typeof candidate.matched_segment.duration === 'number'
                                      ? ` · ${candidate.matched_segment.duration.toFixed(1)}s`
                                      : ''}
                                  </div>
                                  {candidate.matched_segment.video_id && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="mt-1"
                                      onClick={() =>
                                        navigate(
                                          `/content-library/video/${candidate.matched_segment?.video_id}`,
                                        )
                                      }
                                    >
                                      Open existing asset
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 lg:max-w-sm lg:justify-end">
                          {(
                            ['approve', 'replace_matched', 'library_only', 'ignore'] as PromptReviewDecision[]
                          )
                            .filter((d) => d !== 'replace_matched' || Boolean(candidate.matched_segment))
                            .map((d) => (
                              <Button
                                key={d}
                                type="button"
                                variant={decision === d ? 'default' : 'outline'}
                                size="sm"
                                onClick={() =>
                                  setReviewDecisions((cur) => ({
                                    ...cur,
                                    [candidate.segment_id]: d,
                                  }))
                                }
                              >
                                {REVIEW_DECISION_LABELS[d]}
                              </Button>
                            ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border/80 px-4 py-8 text-center text-sm text-muted-foreground">
                No review candidates for this recording.
              </div>
            )}
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button variant="outline" onClick={() => setReviewSessionId(null)}>
              Close
            </Button>
            <Button
              onClick={() => applyReviewMutation.mutate()}
              disabled={
                applyReviewMutation.isPending ||
                reviewQuery.isLoading ||
                !reviewQuery.data?.pending_candidates
              }
              className="gap-2"
            >
              {applyReviewMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <ListChecks className="h-4 w-4" />
                  Apply decisions
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DemoStudioPage;
